import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import PoleListData from './poleListData';
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 500, description: 800
};

const PoleList = React.memo(({ scrollRef, poles, sortBy, setSortBy, setOffset, filteredPoleCount, setPoleFetched }) => {

    const navigate = useNavigate();

    const [poleColumnWidth, setPoleColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('poleColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setPoleColumnWidth({ ...poleColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('poleColumnWidth', JSON.stringify(poleColumnWidth));
    }, [poleColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredPoleCount, setPoleFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setPoleFetched, filteredPoleCount, 70, 280);

    const handlePoleClick = useCallback((poleId) => navigate(`pole_details/${poleId}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { poles, handlePoleClick } = data;
        const pole = index === 0 ? poles[0] : poles[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <PoleListData
                    pole={pole}
                    columnWidth={poleColumnWidth}
                    handlePoleClick={handlePoleClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={poleColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={poleColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredPoleCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredPoleCount}
                    itemSize={70}
                    itemData={{ poles, handlePoleClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default PoleList;